import React, { useEffect, useState } from 'react';
import { get2faStatus, setup2fa, enable2fa, disable2fa, logoutEverywhere } from '../api';
import { ShieldCheck, ShieldOff, Copy, Check, LogOut } from 'lucide-react';
import toast from 'react-hot-toast';
import Button from './Button';

// step: loading | off | setup | codes | on
export default function TwoFactorCard() {
  const [step, setStep]   = useState('loading');
  const [qr, setQr]       = useState('');
  const [secret, setSecret] = useState('');
  const [code, setCode]   = useState('');
  const [codes, setCodes] = useState([]);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    get2faStatus().then(r => setStep(r.enabled ? 'on' : 'off')).catch(() => setStep('off'));
  }, []);

  const startSetup = async () => {
    try {
      const r = await setup2fa();
      setQr(r.qr); setSecret(r.secret); setCode(''); setStep('setup');
    } catch { toast.error('Could not start setup'); }
  };

  const confirmEnable = async () => {
    try {
      const r = await enable2fa(code.replace(/\s/g, ''));
      if (r.token) localStorage.setItem('wpm_token', r.token); // sessions elsewhere are now retired
      setCodes(r.backup_codes || []); setStep('codes');
      toast.success('Two-factor authentication enabled — other devices signed out');
    } catch (err) { toast.error(err?.error || 'Invalid code'); }
  };

  const doDisable = async () => {
    const c = window.prompt('Enter a current 6-digit code (or a backup code) to turn off 2FA:');
    if (!c) return;
    try {
      const r = await disable2fa(c.replace(/\s/g, ''));
      if (r.token) localStorage.setItem('wpm_token', r.token);
      setStep('off'); toast.success('2FA disabled — other devices signed out');
    }
    catch (err) { toast.error(err?.error || 'Invalid code'); }
  };

  const signOutEverywhere = async () => {
    if (!window.confirm('Sign out of every device, including this one?\n\nUse this if a device was lost or a session might have been copied. You will need to sign in again.')) return;
    try {
      await logoutEverywhere();
      localStorage.removeItem('wpm_token');
      window.location.href = '/login';
    } catch (err) { toast.error(err?.error || 'Could not end the sessions'); }
  };

  const copyCodes = () => {
    navigator.clipboard.writeText(codes.join('\n')).then(() => { setCopied(true); setTimeout(() => setCopied(false), 2000); });
  };

  return (
    <div className="card">
      <div className="card-head">
        <h3><ShieldCheck size={14} /> Two-factor authentication</h3>
        {step === 'on' && <span className="badge green">On</span>}
        {step === 'off' && <span className="badge gray">Off</span>}
      </div>
      <div style={{ padding: '14px 18px' }}>
        {step === 'loading' && <div style={{ color: 'var(--text3)', fontSize: 13 }}>Loading…</div>}

        {step === 'off' && (
          <>
            <p style={{ fontSize: 12.5, color: 'var(--text2)', marginBottom: 12 }}>
              Add a second step at login using an authenticator app (Google Authenticator, Authy, 1Password…). Even if your password leaks, nobody can sign in without your phone.
            </p>
            <Button className="btn primary" onClick={startSetup}><ShieldCheck size={13} /> Enable 2FA</Button>
          </>
        )}

        {step === 'setup' && (
          <>
            <p style={{ fontSize: 12.5, color: 'var(--text2)', marginBottom: 12 }}>
              1. Scan this QR with your authenticator app, then enter the 6-digit code it shows.
            </p>
            <div style={{ display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap' }}>
              <img src={qr} alt="2FA QR code" width={148} height={148} style={{ borderRadius: 10, background: '#fff', padding: 6 }} />
              <div style={{ flex: 1, minWidth: 180 }}>
                <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 4 }}>Can’t scan? Enter this key manually:</div>
                <code style={{ display: 'block', fontSize: 12, background: 'var(--bg3)', padding: '7px 10px', borderRadius: 8, wordBreak: 'break-all', marginBottom: 12 }}>{secret}</code>
                <div className="field" style={{ margin: 0 }}>
                  <label>6-digit code</label>
                  <input type="text" inputMode="numeric" placeholder="123456" value={code}
                    onChange={e => setCode(e.target.value.replace(/[^0-9]/g, ''))}
                    style={{ letterSpacing: '3px', textAlign: 'center' }} />
                </div>
                <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                  <Button className="btn primary" onClick={confirmEnable} disabled={code.length < 6}>Verify &amp; enable</Button>
                  <button className="btn" onClick={() => setStep('off')}>Cancel</button>
                </div>
              </div>
            </div>
          </>
        )}

        {step === 'codes' && (
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, background: 'var(--amber-bg)', borderRadius: 10, padding: '10px 12px', marginBottom: 12 }}>
              <span style={{ fontSize: 12.5, color: 'var(--amber-text)' }}>
                <strong>Save these backup codes now.</strong> Each works once, to sign in if you lose your phone. They won’t be shown again.
              </span>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, marginBottom: 12 }}>
              {codes.map(c => (
                <code key={c} style={{ fontSize: 13, background: 'var(--bg3)', padding: '7px 10px', borderRadius: 8, textAlign: 'center', letterSpacing: '1px' }}>{c}</code>
              ))}
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <Button className="btn" onClick={copyCodes}>{copied ? <><Check size={13} /> Copied</> : <><Copy size={13} /> Copy codes</>}</Button>
              <button className="btn primary" onClick={() => setStep('on')}>I’ve saved them</button>
            </div>
          </>
        )}

        {step === 'on' && (
          <>
            <p style={{ fontSize: 12.5, color: 'var(--text2)', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
              <ShieldCheck size={16} color="var(--green)" /> Your account is protected with two-factor authentication.
            </p>
            <Button className="btn danger" onClick={doDisable}><ShieldOff size={13} /> Disable 2FA</Button>
          </>
        )}

        {/* Available whether 2FA is on or not — a stolen session token never
            had to pass 2FA, so ending sessions is the cure for that. */}
        {step !== 'loading' && step !== 'setup' && step !== 'codes' && (
          <div style={{ marginTop: 16, paddingTop: 14, borderTop: '1px solid var(--border)' }}>
            <div style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--text)' }}>Active sessions</div>
            <p style={{ fontSize: 11.5, color: 'var(--text3)', margin: '4px 0 10px', lineHeight: 1.5 }}>
              Signing in leaves a session that lasts 7 days. End them all if a device was lost,
              or if a session might have been copied.
            </p>
            <Button className="btn" onClick={signOutEverywhere}>
              <LogOut size={13} /> Sign out everywhere
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
